<template>
  <div class="today-todo column">
    <div class="flex-1 overflow-y todoitemList" v-if="!isFirstLoading">
      <div class="today-title" v-if="firstVisitInfo.id">
        <span>初诊情况</span>
        <span class="today-title-info">{{ firstVisitInfo.appointTime }}</span>
        <span class="today-title-info">{{ firstVisitInfo.doctorName }}</span>
        <span class="today-title-info">{{
          firstVisitInfo.appointItemNames
        }}</span>
      </div>
      <div v-if="firstVisitInfo.id" class="initial-consultation-main">
        <InitialConsultation :info="firstVisitInfo"></InitialConsultation>
      </div>

      <div class="today-title" v-if="followTasks.length > 0">跟进任务</div>
      <div v-for="item in followTasks" :key="item.id">
        <FollowUpTask :self="item"></FollowUpTask>
      </div>

      <div class="today-title" v-if="returnVisits.length > 0">诊后回访</div>
      <div v-for="item in returnVisits" :key="getTodoReturnVisitRowKey(item)">
        <TodayVisitFollowUp :self="item"></TodayVisitFollowUp>
      </div>

      <div class="today-title" v-if="preConsults.length > 0">诊前沟通</div>
      <div v-for="item in preConsults" :key="getTodoReturnVisitRowKey(item)">
        <TomorrowPreConsultation :self="item"></TomorrowPreConsultation>
      </div>

      <!-- 当三种内容都为空时显示的提示 -->
      <div
        v-if="
          followTasks.length === 0 &&
          returnVisits.length === 0 &&
          preConsults.length === 0
        "
      >
        <div class="today-title">跟进任务</div>
        <div class="no-task-hint">当前没有跟进任务</div>
      </div>
    </div>
    <div class="flex-1" v-else></div>
    <div class="toCreateBtn" @click="toCreate()">新增跟进计划</div>
  </div>
</template>

<script>
//components
import InitialConsultation from "@/components/personalCenter/todayTodo/card/initialConsultation.vue";
import TomorrowPreConsultation from "@/components/personalCenter/todayTodo/card/tomorrowPreConsultation.vue";
import TodayVisitFollowUp from "@/components/personalCenter/todayTodo/card/todayVisitFollowUp.vue";
import FollowUpTask from "@/components/personalCenter/todayTodo/card/followUpTask.vue";
//api
import {
  getPatientTodayTodo,
  getPatientFirstVisit,
} from "@/api/h5/todayTodo.js";
//store
import { todayTodoStore } from "@/store/h5/todayTodo.js";
import { loadingStore } from "@/store/loading.js";
//utils
import moment from "moment";

/** 回访记录状态：与接口 returnVisitStatus 对齐 */
const RETURN_VISIT_STATUS = {
  /** 待回访 / 待沟通 */
  PENDING: 0,
  /** 已回访 / 已沟通 */
  DONE: 1,
};

/**
 * 将列表中的「已回访」记录按预约 id（字段 id）合并为一条展示项；「待回访」记录不合并，保持原顺序逐条展示。
 * 同一预约下多条已回访时，在合并项上挂载 returnVisitHistoryList 供子组件渲染多条沟通记录。
 *
 * @param {Array<Object>} rawReturnVisitList 接口返回的回访/沟通列表
 * @returns {Array<Object>}
 */
function mergeDoneReturnVisitsByAppointId(rawReturnVisitList) {
  if (!Array.isArray(rawReturnVisitList) || rawReturnVisitList.length === 0) {
    return [];
  }

  const doneGroupMap = new Map();
  for (const visitItem of rawReturnVisitList) {
    if (visitItem.returnVisitStatus === RETURN_VISIT_STATUS.DONE) {
      const appointId = visitItem.id;
      if (!doneGroupMap.has(appointId)) {
        doneGroupMap.set(appointId, []);
      }
      doneGroupMap.get(appointId).push(visitItem);
    }
  }

  const emittedDoneAppointIdSet = new Set();
  const mergedList = [];

  for (const visitItem of rawReturnVisitList) {
    if (visitItem.returnVisitStatus !== RETURN_VISIT_STATUS.DONE) {
      mergedList.push({ ...visitItem });
      continue;
    }

    const appointId = visitItem.id;
    if (emittedDoneAppointIdSet.has(appointId)) {
      continue;
    }
    emittedDoneAppointIdSet.add(appointId);

    const groupRecords = doneGroupMap.get(appointId) || [];
    const sortedRecords = groupRecords.slice().sort((a, b) => {
      const idStrA = a.returnVisitId != null ? String(a.returnVisitId) : "";
      const idStrB = b.returnVisitId != null ? String(b.returnVisitId) : "";
      return idStrA.localeCompare(idStrB, undefined, { numeric: true });
    });

    const baseItem = { ...sortedRecords[sortedRecords.length - 1] };
    if (sortedRecords.length > 1) {
      baseItem.returnVisitHistoryList = sortedRecords;
    }
    mergedList.push(baseItem);
  }

  return mergedList;
}

export default {
  name: "TodayTodo",
  components: {
    InitialConsultation,
    TomorrowPreConsultation,
    TodayVisitFollowUp,
    FollowUpTask,
  },
  props: {
    patientId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // 今日跟进任务列表
      followTasks: [],
      // 明日就诊诊前沟通列表
      preConsults: [],
      // 今日就诊回访列表
      returnVisits: [],
      // 初诊信息
      firstVisitInfo: {},
      // 第一次加载状态
      isFirstLoading: true,
    };
  },
  setup() {
    const loading = loadingStore();
    return {
      loading,
    };
  },
  mounted() {
    this.getList();
  },
  methods: {
    /**
     * 获取今日待办事项列表
     * 在加载过程中隐藏 todoitemList，加载完成后显示
     */
    getList() {
      const that = this;

      this.loading.showLoading();

      Promise.all([
        getPatientTodayTodo({
          patientId: that.patientId,
        }),
        getPatientFirstVisit({
          patientId: that.patientId,
        }),
      ])
        .then((res) => {
          //跟进任务,诊前沟通,诊后回访
          let { followTasks, preConsults, returnVisits } = res[0].data.data;
          //初诊情况
          let firstVisitInfo = res[1].data.data || {};
          //处理时间格式
          firstVisitInfo.appointTime = moment(
            firstVisitInfo.appointTime,
          ).format("YYYY-MM-DD");

          that.followTasks = followTasks; //跟进任务
          that.preConsults = mergeDoneReturnVisitsByAppointId(
            preConsults || [],
          );
          that.returnVisits = mergeDoneReturnVisitsByAppointId(
            returnVisits || [],
          );

          that.firstVisitInfo = firstVisitInfo; //初诊情况
        })
        .finally(() => {
          that.isFirstLoading = false;
          that.loading.hideLoading();
        });
    },
    /**
     * 跳转到新增跟进计划页面
     * 使用 Vue Router 进行页面跳转
     */
    toCreate() {
      const todayTodoStoreInstance = todayTodoStore();
      todayTodoStoreInstance.clearCurrentFollowUp();
      this.$router.push("/todayTodo/create?status=1");
    },
    /**
     * v-for 唯一 key：已合并的多条已回访共用预约 id；待回访用预约 id + 回访记录 id 区分
     */
    getTodoReturnVisitRowKey(todoItem) {
      if (
        todoItem.returnVisitHistoryList &&
        todoItem.returnVisitHistoryList.length > 1
      ) {
        return `todo-rv-merged-${todoItem.id}`;
      }
      return `todo-rv-${todoItem.id}-${todoItem.returnVisitId || "pending"}`;
    },
  },
};
</script>

<style scoped>
.today-todo {
  text-align: left;
  font-size: 0.292rem;
  box-sizing: border-box;
  flex: 1;
  height: 100px;
  padding-bottom: 0.25rem;
}

.initial-consultation-main {
  margin-bottom: 0.208rem;
}

.today-title {
  margin-bottom: 0.208rem;
}

.today-title .today-title-info {
  margin-left: 0.208rem;
  color: #666;
}

.no-task-hint {
  color: #999;
  font-size: 0.25rem;
  text-align: center;
  padding: 1rem 0;
  background-color: #fff;
}

.toCreateBtn {
  background-color: #28b8c3;
  color: #fff;
  padding: 0.208rem;
  border-radius: 0.083rem;
  text-align: center;
}
</style>
